import React, { useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useLocation, useNavigate } from 'react-router-dom';

/* ─── SVG Icons ─── */
const Icon = ({ name, size = 18, color = 'currentColor', style = {} }) => {
  const s = { width: size, height: size, display: 'block', ...style };
  const props = { viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' };
  const map = {
    download: <svg {...props} style={s}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
    arrowLeft: <svg {...props} style={s}><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>,
    loader: <svg {...props} style={{ ...s, animation: 'ds-spin 1s linear infinite' }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>,
    alert: <svg {...props} style={s}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
    baby: <svg {...props} style={s}><circle cx="12" cy="6" r="3" /><path d="M9 12H7a5 5 0 000 10h10a5 5 0 000-10h-2" /><line x1="12" y1="9" x2="12" y2="12" /></svg>,
    heart: <svg {...props} style={s}><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" /></svg>,
    activity: <svg {...props} style={s}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
    dna: <svg {...props} style={s}><path d="M2 15c6.667-6 13.333 0 20-6" /><path d="M9 22c1.798-1.998 2.518-3.995 2.807-5.993" /><path d="M15 2c-1.798 1.998-2.518 3.995-2.807 5.993" /><path d="M2 9c6.667 6 13.333 0 20 6" /></svg>,
    clipboard: <svg {...props} style={s}><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /></svg>,
    leaf: <svg {...props} style={s}><path d="M17 8C8 10 5.9 16.17 3.82 19.97M12 12c0 2.76-.5 5.5-1.5 8M21 3c-2.3 3.15-4.5 5-7.5 5.5" /></svg>,
  };
  return map[name] || null;
};

/* ─── Section metadata ─── */
const SECTION_META = {
  s1: { title: 'Basic  Information', icon: 'baby', accent: '#4F46E5' },
  s2: { title: 'Medical History & Allergies', icon: 'heart', accent: '#B91C1C' },
  s3: { title: 'Previous Pregnancy Complications', icon: 'activity', accent: '#C2410C' },
  s4: { title: 'Family & Genetic History', icon: 'dna', accent: '#0369A1' },
  s5: { title: 'Current Symptoms', icon: 'clipboard', accent: '#047857' },
  s6: { title: 'Danger Signs', icon: 'alert', accent: '#9F1239' },
  s7: { title: 'Nutrition & Supplements', icon: 'leaf', accent: '#166534' },
};

const getSectionMeta = (key) =>
  SECTION_META[key] || { title: `Section ${key.toUpperCase()}`, icon: 'clipboard', accent: '#017564' };

const formatLabel = (label) =>
  label.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());

const formatValue = (value) => {
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
  return String(value ?? '—');
};

/* ─── Screen Value Renderer — no color logic based on content ─── */
const ValueRenderer = ({ value }) => {
  if (Array.isArray(value)) {
    if (!value.length) return <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>None</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
        {value.map((v, i) => (
          <span key={i} style={{
            background: '#F1F5F9', color: '#334155',
            padding: '2px 9px', borderRadius: '5px', fontSize: '12px',
            fontWeight: 500, border: '1px solid #E2E8F0',
          }}>{v}</span>
        ))}
      </div>
    );
  }
  const str = String(value ?? '');
  if (!str || str === 'undefined') return <span style={{ color: '#CBD5E1' }}>—</span>;
  return <span style={{ color: '#0F172A', fontWeight: 500 }}>{str}</span>;
};

/* ─── PDF HTML builder — mirrors screen exactly ─── */
// export const buildPdfHtml = (data, title, reportId, date) => {
//   const sectionsHtml = Object.entries(data).map(([key, section], idx) => {
//     const meta = getSectionMeta(key);
//     const rows = Object.entries(section);
//     const paired = [];
//     for (let i = 0; i < rows.length; i += 2) paired.push([rows[i], rows[i + 1] || null]);

//     const tableRows = paired.map(([left, right]) => `
//       <tr>
//         <td style="width:50%;padding:10px 14px;vertical-align:top;border-bottom:1px solid #F1F5F9;border-right:1px solid #F1F5F9">
//           <div style="font-size:9px;color:#94A3B8;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:3px">${formatLabel(left[0])}</div>
//           <div style="font-size:13px;color:#0F172A;font-weight:500;line-height:1.4">${formatValue(left[1])}</div>
//         </td>
//         <td style="width:50%;padding:10px 14px;vertical-align:top;border-bottom:1px solid #F1F5F9">
//           ${right ? `
//           <div style="font-size:9px;color:#94A3B8;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:3px">${formatLabel(right[0])}</div>
//           <div style="font-size:13px;color:#0F172A;font-weight:500;line-height:1.4">${formatValue(right[1])}</div>
//           ` : ''}
//         </td>
//       </tr>`).join('');

//     return `
//       <div style="border:1px solid #E2E8F0;border-radius:8px;overflow:hidden;margin-bottom:18px">
//         <div style="background:#F8FAFC;padding:10px 14px;display:flex;align-items:center;gap:8px;border-bottom:1px solid #E2E8F0">
//           <span style="font-family:monospace;font-size:10px;color:#94A3B8;font-weight:600">${String(idx + 1).padStart(2, '0')}</span>
//           <span style="width:5px;height:5px;border-radius:50%;background:${meta.accent};display:inline-block"></span>
//           <span style="font-size:11px;font-weight:700;color:#0F172A;text-transform:uppercase;letter-spacing:0.8px">${meta.title}</span>
//         </div>
//         <table style="width:100%;border-collapse:collapse;background:white">${tableRows}</table>
//       </div>`;
//   }).join('');

//   return `
//     <div style="font-family:'Segoe UI',Arial,sans-serif;background:#F0F4F8;padding:48px 52px;min-width:820px;max-width:920px">
//       <div style="background:white;border-radius:12px;overflow:hidden;box-shadow:0 2px 12px rgba(15,23,42,0.08)">

//         <!-- Letterhead -->
//         <div style="padding:28px 32px 20px;border-bottom:2.5px solid #017564;display:flex;justify-content:space-between;align-items:flex-start">
//           <div>
//             <div style="font-size:9px;letter-spacing:2.5px;text-transform:uppercase;color:#64748B;font-weight:600;margin-bottom:6px">Medical Assessment Report</div>
//             <div style="font-size:20px;font-weight:800;color:#0F172A;line-height:1.3">${title}</div>
//           </div>
//           <div style="text-align:right">
//             <div style="font-size:9px;color:#94A3B8;font-weight:500;margin-bottom:2px">Report ID</div>
//             <div style="font-family:monospace;font-size:12px;font-weight:700;color:#017564">${reportId}</div>
//             <div style="font-size:10px;color:#64748B;margin-top:6px">${date}</div>
//           </div>
//         </div>

//         <!-- Disclaimer -->
//         <div style="margin:16px 32px 0">
//           <div style="background:#E6F5F2;border-left:3px solid #017564;padding:10px 14px;border-radius:0 6px 6px 0">
//             <div style="font-size:9px;font-weight:700;color:#017564;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:2px">For Clinical Use</div>
//             <div style="font-size:11px;color:#017564;line-height:1.5">This is a system-generated summary. Please review with a qualified healthcare professional before clinical decision-making.</div>
//           </div>
//         </div>

//         <!-- Sections -->
//         <div style="padding:24px 32px 28px">${sectionsHtml}</div>

//         <!-- Footer -->
//         <div style="display:flex;justify-content:space-between;align-items:center;padding:12px 32px;border-top:1px solid #E2E8F0;background:#F8FAFC">
//           <span style="font-size:9px;color:#94A3B8">Auto-generated · ${date}</span>
//           <span style="font-family:monospace;font-size:9px;color:#CBD5E1">${reportId}</span>
//         </div>
//       </div>
//     </div>`;
// };
// Add this helper at the top of DynamicSummary.jsx

// export const buildPdfHtml = (data, title, reportId, date, logoBase64 = '') => {
//   const logoHtml = logoBase64
//     ? `<img src="${logoBase64}" style="height:40px;object-fit:contain;display:block" />`
//     : `<div style="font-size:16px;font-weight:800;color:#017564">Health Report</div>`;

//   const sectionsHtml = Object.entries(data).map(([key, section], idx) => {
//     const meta = getSectionMeta(key);
//     const rows = Object.entries(section);
//     const paired = [];
//     for (let i = 0; i < rows.length; i += 2) paired.push([rows[i], rows[i + 1] || null]);

//     const tableRows = paired.map(([left, right]) => `
//       <tr>
//         <td style="width:50%;padding:10px 14px;vertical-align:top;border-bottom:1px solid #F1F5F9;border-right:1px solid #F1F5F9">
//           <div style="font-size:9px;color:#94A3B8;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:3px">${formatLabel(left[0])}</div>
//           <div style="font-size:13px;color:#0F172A;font-weight:500;line-height:1.4">${formatValue(left[1])}</div>
//         </td>
//         <td style="width:50%;padding:10px 14px;vertical-align:top;border-bottom:1px solid #F1F5F9">
//           ${right ? `
//           <div style="font-size:9px;color:#94A3B8;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:3px">${formatLabel(right[0])}</div>
//           <div style="font-size:13px;color:#0F172A;font-weight:500;line-height:1.4">${formatValue(right[1])}</div>
//           ` : ''}
//         </td>
//       </tr>`).join('');

//     return `
//       <div style="border:1px solid #E2E8F0;border-radius:8px;overflow:hidden;margin-bottom:18px">
//         <div style="background:#F8FAFC;padding:10px 14px;display:flex;align-items:center;gap:8px;border-bottom:1px solid #E2E8F0">
//           <span style="font-family:monospace;font-size:10px;color:#94A3B8;font-weight:600">${String(idx + 1).padStart(2, '0')}</span>
//           <span style="width:5px;height:5px;border-radius:50%;background:${meta.accent};display:inline-block"></span>
//           <span style="font-size:11px;font-weight:700;color:#0F172A;text-transform:uppercase;letter-spacing:0.8px">${meta.title}</span>
//         </div>
//         <table style="width:100%;border-collapse:collapse;background:white">${tableRows}</table>
//       </div>`;
//   }).join('');

//   return `
//     <div style="font-family:'Segoe UI',Arial,sans-serif;background:#F0F4F8;padding:48px 52px;min-width:820px;max-width:920px">
//       <div style="background:white;border-radius:12px;overflow:hidden;box-shadow:0 2px 12px rgba(15,23,42,0.08)">

//         <!-- Letterhead -->
//         <div style="padding:28px 32px 20px;border-bottom:2.5px solid #017564;display:flex;justify-content:space-between;align-items:center">
//           <div style="display:flex;align-items:center;gap:16px">
//             ${logoHtml}
//             <div>
//               <div style="font-size:9px;letter-spacing:2.5px;text-transform:uppercase;color:#64748B;font-weight:600;margin-bottom:4px">Health Assessment Report</div>
//               <div style="font-size:20px;font-weight:800;color:#0F172A;line-height:1.3">${title}</div>
//             </div>
//           </div>
//           <div style="text-align:right;flex-shrink:0">
//             <div style="font-size:9px;color:#94A3B8;font-weight:500;margin-bottom:2px">Report ID</div>
//             <div style="font-family:monospace;font-size:12px;font-weight:700;color:#017564">${reportId}</div>
//             <div style="font-size:10px;color:#64748B;margin-top:6px">${date}</div>
//           </div>
//         </div>

//         <!-- Disclaimer -->
//         <div style="margin:16px 32px 0">
//           <div style="background:#E6F5F2;border-left:3px solid #017564;padding:10px 14px;border-radius:0 6px 6px 0">
//             <div style="font-size:9px;font-weight:700;color:#017564;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:2px">For Clinical Use</div>
//             <div style="font-size:11px;color:#017564;line-height:1.5">This is a system-generated summary. Please review with a qualified healthcare professional before clinical decision-making.</div>
//           </div>
//         </div>

//         <!-- Sections -->
//         <div style="padding:24px 32px 28px">${sectionsHtml}</div>

//         <!-- Footer -->
//         <div style="display:flex;justify-content:space-between;align-items:center;padding:12px 32px;border-top:1px solid #E2E8F0;background:#F8FAFC">
//           <span style="font-size:9px;color:#94A3B8">Auto-generated · ${date}</span>
//           <span style="font-family:monospace;font-size:9px;color:#CBD5E1">${reportId}</span>
//         </div>
//       </div>
//     </div>`;
// };
export const buildPdfHtml = (data, title, reportId, date, logoBase64 = '') => {
  const logoHtml = logoBase64
    ? `<img src="${logoBase64}" style="height:32px;object-fit:contain;display:block" />`
    : `<div style="font-size:15px;font-weight:800;color:#017564">Mukta Women's Clinic</div>`;

  const sectionsHtml = Object.entries(data).map(([key, section], idx) => {
    const meta = getSectionMeta(key);
    const rows = Object.entries(section);
    const paired = [];
    for (let i = 0; i < rows.length; i += 2) paired.push([rows[i], rows[i + 1] || null]);

    const tableRows = paired.map(([left, right]) => `
      <tr>
        <td style="width:50%;padding:10px 14px;vertical-align:top;border-bottom:1px solid #F1F5F9;border-right:1px solid #F1F5F9">
          <div style="font-size:9px;color:#94A3B8;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:3px">${formatLabel(left[0])}</div>
          <div style="font-size:13px;color:#0F172A;font-weight:500;line-height:1.4">${formatValue(left[1])}</div>
        </td>
        <td style="width:50%;padding:10px 14px;vertical-align:top;border-bottom:1px solid #F1F5F9">
          ${right ? `
          <div style="font-size:9px;color:#94A3B8;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:3px">${formatLabel(right[0])}</div>
          <div style="font-size:13px;color:#0F172A;font-weight:500;line-height:1.4">${formatValue(right[1])}</div>
          ` : ''}
        </td>
      </tr>`).join('');

    return `
      <div style="border:1px solid #E2E8F0;border-radius:8px;overflow:hidden;margin-bottom:18px">
        <div style="background:#F8FAFC;padding:10px 14px;display:flex;align-items:center;gap:8px;border-bottom:1px solid #E2E8F0">
          <span style="font-family:monospace;font-size:10px;color:#94A3B8;font-weight:600">${String(idx + 1).padStart(2, '0')}</span>
          <span style="width:5px;height:5px;border-radius:50%;background:${meta.accent};display:inline-block"></span>
          <span style="font-size:11px;font-weight:700;color:#0F172A;text-transform:uppercase;letter-spacing:0.8px">${meta.title}</span>
        </div>
        <table style="width:100%;border-collapse:collapse;background:white">${tableRows}</table>
      </div>`;
  }).join('');

  return `
    <div style="font-family:'Segoe UI',Arial,sans-serif;background:#F0F4F8;padding:40px;min-width:680px;max-width:780px">
      <div style="background:white;border-radius:12px;overflow:hidden;box-shadow:0 2px 12px rgba(15,23,42,0.08)">

        <!-- Letterhead -->
        <!-- Letterhead -->
<div style="padding:24px 32px 20px;">

  <!-- Top row: logo left, date right -->
  <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:14px">
    ${logoHtml}
    <div style="font-size:10px;color:#64748B">${date}</div>
  </div>

  <!-- Divider + title block -->
  <div style="border-top:2.5px solid #017564;padding-top:12px">
    <div style="font-size:9px;letter-spacing:3px;text-transform:uppercase;color:#94A3B8;font-weight:600;margin-bottom:5px">
      Health Assessment Report
    </div>
    <div style="font-size:22px;font-weight:800;color:#0F172A;line-height:1.2">
      ${title}
    </div>
  </div>

</div>
        <!-- Disclaimer -->
        <div style="margin:16px 32px 0">
          <div style="background:#E6F5F2;border-left:3px solid #017564;padding:10px 14px;border-radius:0 6px 6px 0">
            <div style="font-size:9px;font-weight:700;color:#017564;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:2px">For Clinical Use</div>
            <div style="font-size:11px;color:#017564;line-height:1.5">This is a system-generated summary. Please review with a qualified healthcare professional before clinical decision-making.</div>
          </div>
        </div>

        <!-- Sections -->
        <div style="padding:24px 32px 28px">${sectionsHtml}</div>

        <!-- Footer -->
        <div style="display:flex;justify-content:space-between;align-items:center;padding:12px 32px;border-top:1px solid #E2E8F0;background:#F8FAFC">
          <span style="font-size:9px;color:#94A3B8">Auto-generated · ${date}</span>
          <span style="font-family:monospace;font-size:9px;color:#CBD5E1">${reportId}</span>
        </div>

      </div>
    </div>`;
};
export const getLogoBase64 = () => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(''); // fallback: no logo
    img.src = '/Home/logo.svg';
  });
};

/* ─── Chevron ─── */
const Chevron = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transition: 'transform .2s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', display: 'block' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/* ─── Main Component ─── */
const DynamicSummary = ({ data, title = 'Form Summary', onBack }) => {
  console.log(onBack, "this is onback")
  const contentRef = useRef(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  //   const [openMap, setOpenMap] = useState({});   
  //   const [allOpen, setAllOpen] = useState(true);
  const firstSectionKey = Object.keys(data || {})[0];

  const [openMap, setOpenMap] = useState({
    [firstSectionKey]: true,
  });

  const [allOpen, setAllOpen] = useState(false);
  const reportId = useRef(`RPT-${Date.now().toString(36).toUpperCase()}`).current;
  const date = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

  const sections = Object.entries(data).map(([key, section], idx) => ({
    key, meta: getSectionMeta(key), idx, fields: Object.entries(section),
  }));

  const isOpen = (key) => openMap[key] ?? allOpen;
  const toggleSection = (key) => setOpenMap(m => ({ ...m, [key]: !isOpen(key) }));
  // const toggleAll = () => { setAllOpen(o => !o); setOpenMap({}); };
  const logoBase64Ref = useRef('');

  useEffect(() => {
    getLogoBase64().then(b64 => { logoBase64Ref.current = b64; });
  }, []);
  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const logoBase64 = await getLogoBase64()
      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = 'position:fixed;left:-9999px;top:0;z-index:-1';
      // tempDiv.innerHTML = buildPdfHtml(data, title, reportId, date);
      // In downloadPDF, pass the base64 logo:
      tempDiv.innerHTML = buildPdfHtml(data, title, reportId, date, logoBase64);
      document.body.appendChild(tempDiv);
      await new Promise(r => setTimeout(r, 150));

      const canvas = await html2canvas(tempDiv, { scale: 2, useCORS: true, backgroundColor: '#F0F4F8' });
      document.body.removeChild(tempDiv);

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin = 10;
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const usableW = pdfW - margin * 2;
      const usableH = pdfH - margin * 2;
      const scale = usableW / canvas.width;
      const sliceH = usableH / scale;

      let srcY = 0; let page = 0;
      while (srcY < canvas.height) {
        if (page > 0) pdf.addPage();
        const h = Math.min(sliceH, canvas.height - srcY);
        const slice = document.createElement('canvas');
        slice.width = canvas.width; slice.height = h;
        slice.getContext('2d').drawImage(canvas, 0, -srcY);
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', margin, margin, usableW, h * scale);
        srcY += sliceH; page++;
      }
      pdf.save(`${title.replace(/\s+/g, '_')}_${reportId}.pdf`);
    } catch (err) {
      console.error('PDF error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        @keyframes ds-spin { to { transform:rotate(360deg); } }
 /* ── Fixed Bottom Bar ── */
.ds-bottom-bar {
  position: fixed;
  left: 50%;
  bottom: 16%;
  transform: translateX(-50%);
  z-index: 1000;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  width: calc(100% - 32px);
  max-width: 860px;
  padding: 12px 16px;

  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: 16px;

  box-shadow: 
    0 10px 30px rgba(15, 23, 42, 0.15),
    0 4px 12px rgba(15, 23, 42, 0.08);
}

/* Buttons */
.ds-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  background: white;
  color: #475569;
  border: 1.5px solid #CBD5E1;
  cursor: pointer;
  transition: all 0.15s;
}

.ds-back-btn:hover {
  background: #F8FAFC;
  border-color: #94A3B8;
}

.ds-dl-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  background: #017564;
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
}

.ds-dl-btn:hover:not(:disabled) {
  background: #1640B8;
  box-shadow: 0 4px 15px rgba(27, 79, 216, 0.3);
}

.ds-dl-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Extra bottom padding for scroll area */
.ds-scroll {
  flex: 1 1 0;
  overflow-y: auto;
  padding: 20px 16px 100px;   /* Important: prevents overlap */
}
        /* ── Fixed-height shell — flex column ── */
        .ds-page {
          height: 100vh;
          display: flex;
          flex-direction: column;
          background: #F0F4F8;
          font-family: 'Inter', system-ui, sans-serif;
          overflow: hidden;
        }

        /* ── Sticky top bar ── */
        .ds-topbar {
          flex-shrink: 0;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 10px;
          padding: 12px 20px;
          background: #F0F4F8;
          border-bottom: 1px solid #E2E8F0;
        }
        .ds-topbar-left  { display:flex; align-items:center; gap:8px; }
        .ds-topbar-right { display:flex; align-items:center; gap:8px; }

        .ds-back-btn {
          display:inline-flex; align-items:center; gap:6px; padding:7px 14px;
          border-radius:8px; font-size:13px; font-weight:600;
          background:white; color:#475569; border:1.5px solid #CBD5E1;
          cursor:pointer; font-family:inherit; transition:all .15s;
        }
        .ds-back-btn:hover { background:#F8FAFC; border-color:#94A3B8; }

        .ds-toggle-all-btn {
          display:inline-flex; align-items:center; gap:6px; padding:7px 14px;
          border-radius:8px; font-size:12px; font-weight:600;
          background:white; color:#475569; border:1.5px solid #CBD5E1;
          cursor:pointer; font-family:inherit; transition:all .15s;
        }
        .ds-toggle-all-btn:hover { background:#F8FAFC; }

        .ds-dl-btn {
          display:inline-flex; align-items:center; gap:7px; padding:8px 18px;
          border-radius:8px; font-size:13px; font-weight:600;
          background:#017564; color:white; border:none;
          cursor:pointer; font-family:inherit; transition:all .15s;
        }
        .ds-dl-btn:hover:not(:disabled) { background:#1640B8; box-shadow:0 4px 12px rgba(27,79,216,.28); }
        .ds-dl-btn:disabled { opacity:.6; cursor:not-allowed; }

        /* ── Scrollable area — takes remaining height ── */
        .ds-scroll {
          flex: 1 1 0;
          overflow-y: auto;
          padding: 20px 16px 40px;
        }
        .ds-scroll::-webkit-scrollbar { width:5px; }
        .ds-scroll::-webkit-scrollbar-track { background:transparent; }
        .ds-scroll::-webkit-scrollbar-thumb { background:#CBD5E1; border-radius:99px; }
        .ds-scroll::-webkit-scrollbar-thumb:hover { background:#94A3B8; }

        .ds-wrap { max-width:860px; margin:0 auto; height:80vh}

        /* ── Document card ── */
        .ds-doc { background:white; border-radius:12px; box-shadow:0 1px 4px rgba(15,23,42,.07),0 8px 32px rgba(15,23,42,.06); overflow:hidden; height:inherit; overflow:scroll }

        /* Letterhead */
        .ds-lh { padding:24px 32px 18px; border-bottom:2.5px solid #017564; display:flex; justify-content:space-between; align-items:flex-start; flex-wrap:wrap; gap:14px; }
        .ds-lh-eyebrow { font-size:10px; letter-spacing:2.5px; text-transform:uppercase; color:#64748B; font-weight:600; margin-bottom:5px; }
        .ds-lh-title { font-size:clamp(15px,2.5vw,20px); font-weight:800; color:#0F172A; line-height:1.3; }
        .ds-lh-meta { text-align:right; }
        .ds-lh-date { font-size:11px; color:#64748B; }

        /* Disclaimer */
        .ds-disclaimer { margin:14px 32px 0; padding:9px 13px; background:#E6F5F2; border-left:3px solid #017564; border-radius:0 6px 6px 0; }
        .ds-disclaimer-head { font-size:10px; font-weight:700; color:#017564; text-transform:uppercase; letter-spacing:.5px; margin-bottom:2px; }
        .ds-disclaimer-body { font-size:11px; color:#017564; line-height:1.5; }

        /* Sections */
        .ds-sections { padding:18px 32px 28px; display:flex; flex-direction:column; gap:10px; }
        .ds-section { border:1px solid #E2E8F0; border-radius:8px; overflow:hidden; }

        /* Section toggle button */
        .ds-section-toggle {
          width:100%; display:flex; align-items:center; justify-content:space-between;
          padding:10px 14px; background:#F8FAFC;
          border:none; border-bottom:1px solid transparent;
          cursor:pointer; font-family:inherit; transition:background .15s;
          text-align:left;
        }
        .ds-section-toggle[aria-expanded="true"] { border-bottom-color:#E2E8F0; }
        .ds-section-toggle:hover { background:#F1F5F9; }

        .ds-section-left  { display:flex; align-items:center; gap:8px; min-width:0; }
        .ds-section-right { display:flex; align-items:center; gap:8px; flex-shrink:0; margin-left:12px; }
        .ds-section-num   { font-family:'JetBrains Mono',monospace; font-size:10px; font-weight:600; color:#94A3B8; flex-shrink:0; }
        .ds-section-dot   { width:5px; height:5px; border-radius:50%; flex-shrink:0; }
        .ds-section-title { font-size:11px; font-weight:700; color:#0F172A; text-transform:uppercase; letter-spacing:.8px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .ds-section-count { font-size:10px; color:#94A3B8; font-weight:500; white-space:nowrap; }
        .ds-chevron       { display:flex; align-items:center; color:#94A3B8; }

        /* Collapsible body */
        .ds-section-body          { display:none; }
        .ds-section-body--open    { display:block; }

        /* Field grid */
        .ds-grid { display:grid; grid-template-columns:repeat(2,1fr); }
        .ds-field { padding:10px 14px; border-bottom:1px solid #F1F5F9; border-right:1px solid #F1F5F9; display:flex; gap:1rem }
        .ds-field:nth-child(2n)           { border-right:none; }
        .ds-field:last-child,
        .ds-field:nth-last-child(2):nth-child(odd) { border-bottom:none; }
        .ds-field-label { font-size:10px; font-weight:600; color:#94A3B8; text-transform:uppercase; width:40%; letter-spacing:.5px; margin-bottom:4px; }
        .ds-field-value { font-size:13px; color:#0F172A; font-weight:500; line-height:1.4; }

        /* Footer */
        .ds-footer { display:flex; justify-content:space-between; align-items:center; padding:11px 32px; border-top:1px solid #E2E8F0; background:#F8FAFC; flex-wrap:wrap; gap:8px; }
        .ds-footer-l { font-size:10px; color:#94A3B8; }
        .ds-footer-r { font-family:'JetBrains Mono',monospace; font-size:10px; color:#CBD5E1; }

        /* Responsive */
        @media (max-width:560px) {
          .ds-topbar    { padding:10px 14px; }
          .ds-lh        { padding:18px 16px; }
          .ds-lh-meta   { text-align:left; }
          .ds-disclaimer{ margin:12px 16px 0; }
          .ds-sections  { padding:14px 16px 24px; }
          .ds-grid      { grid-template-columns:1fr; }
          .ds-field     { border-right:none !important; }
          .ds-field:nth-last-child(-n+2) { border-bottom:1px solid #F1F5F9; }
          .ds-field:last-child { border-bottom:none !important; }
          .ds-footer    { padding:10px 16px; }
          .ds-section-count { display:none; }
        }
      `}</style>

      <div className="ds-page">

        {/* ── Sticky top bar ── */}
        {/* <div className="ds-topbar"> */}
        {/* <div className="ds-bottom-bar">
            
            <button className="ds-back-btn"
           
              onClick={() => {
                console.log('BACK CLICKED');
                onBack?.();
              }}
            >
              <Icon name="arrowLeft" size={14} /> Back
            </button>
           
          </div> */}
        {/* Fixed Bottom Action Bar */}
        <div className="ds-bottom-bar">
          <button className="ds-back-btn" onClick={() => onBack?.()}>
            <Icon name="arrowLeft" size={16} />
            Back
          </button>

          <button className="btn-primary" onClick={downloadPDF} disabled={pdfLoading}>
            {pdfLoading ? (
              <>
                <Icon name="loader" size={16} color="white" />
                Generating…
              </>
            ) : (
              <>
                <Icon name="download" size={16} color="white" />
                Download PDF
              </>
            )}
          </button>
        </div>
        {/* <div className="ds-">
            <button className="ds-dl-btn" onClick={downloadPDF} disabled={pdfLoading}>
              {pdfLoading
                ? <><Icon name="loader" size={14} color="white" /> Generating…</>
                : <><Icon name="download" size={14} color="white" /> Download PDF</>}
            </button>
          </div> */}
        {/* </div> */}

        {/* ── Scrollable document area ── */}
        <div className="ds-scroll">
          <div className="ds-wrap">
            <div className="ds-doc" ref={contentRef}>

              {/* Letterhead */}
              <div className="ds-lh">
                <div>
                  <img src='/Home/logo.svg' alt='logo' style={{height:"5rem"}} />
                </div>
                <div>
                  <div className="ds-lh-eyebrow">Health Assessment Report</div>
                  <div className="ds-lh-title">{title}</div>
                </div>
                <div className="ds-lh-meta">
                  <div className="ds-lh-date">{date}</div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="ds-disclaimer">
                <div className="ds-disclaimer-head">For Clinical Use</div>
                <div className="ds-disclaimer-body">
                  This is a system-generated summary. Please review with a qualified healthcare professional before clinical decision-making.
                </div>
              </div>

              {/* Collapsible sections */}
              <div className="ds-sections">
                {sections.map(({ key, meta, idx, fields }) => {
                  const open = isOpen(key);
                  return (
                    <div key={key} className="ds-section">
                      <button
                        className="ds-section-toggle"
                        onClick={() => toggleSection(key)}
                        aria-expanded={open}
                      >
                        <span className="ds-section-left">
                          <span className="ds-section-num">{String(idx + 1).padStart(2, '0')}</span>
                          <span className="ds-section-dot" style={{ background: meta.accent }} />
                          <Icon name={meta.icon} size={13} color={meta.accent} />

                          <span className="ds-section-title">{key}</span>

                        </span>
                        <span className="ds-section-right">
                          <span className="ds-section-count">{fields.length} fields</span>
                          <span className="ds-chevron" style={{ color: open ? meta.accent : '#94A3B8' }}>
                            <Chevron open={open} />
                          </span>
                        </span>
                      </button>

                      <div className={`ds-section-body${open ? ' ds-section-body--open' : ''}`}>
                        <div className="ds-grid">
                          {fields.map(([label, value]) => (
                            <div key={label} className="ds-field">
                              <div className="ds-field-label">{formatLabel(label)}</div>
                              <div className="ds-field-value"><ValueRenderer value={value} /></div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="ds-footer">
                <span className="ds-footer-l">Auto-generated · {date} · {sections.length} sections</span>
                <span className="ds-footer-r">{reportId}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ─── ViewResults page ─── */
const ViewResults = ({ answers, assessmentType, onBack }) => {
  const location = useLocation();
  const navigate = useNavigate();
  // const { assessmentType, answers } = location.state || {};

  if (!answers) {
    return (
      <>
        <style>{`
          .vr-empty { min-height:100vh;display:flex;align-items:center;justify-content:center;background:#F0F4F8;font-family:'Inter',system-ui,sans-serif; }
          .vr-box { background:white;padding:48px 40px;border-radius:12px;box-shadow:0 4px 24px rgba(15,23,42,.08);max-width:360px;width:90%;text-align:center; }
          .vr-icon-wrap { width:52px;height:52px;background:#FEF2F2;border-radius:12px;display:flex;align-items:center;justify-content:center;margin:0 auto 18px; }
          .vr-h { font-size:18px;font-weight:800;color:#0F172A;margin-bottom:8px; }
          .vr-p { font-size:13px;color:#64748B;line-height:1.6;margin-bottom:24px; }
          .vr-btn { display:inline-flex;align-items:center;gap:8px;padding:10px 22px;background:#017564;color:white;border:none;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;font-family:inherit; }
          .vr-btn:hover { background:#1640B8; }
        `}</style>
        <div className="vr-empty">
          <div className="vr-box">
            <div className="vr-icon-wrap"><Icon name="alert" size={24} color="#DC2626" /></div>
            <div className="vr-h">No Data Found</div>
            <p className="vr-p">Please complete the assessment first before viewing the report.</p>
            <button className="vr-btn" onClick={() => navigate(-1)}>
              <Icon name="arrowLeft" size={15} color="white" /> Go Back
            </button>
          </div>
        </div>
      </>
    );
  }

  const getPageTitle = () => {
    const t = assessmentType?.toLowerCase() || '';
    if (t.includes('pregnancy')) return 'Pregnancy Risk Assessment';
    if (t === 'general') return 'General Health Assessment';
    return 'Clinical Assessment Report';
  };

  return <DynamicSummary data={answers} title={getPageTitle()}
    onBack={onBack}
  // onBack={() => navigate(-1, { state: { restoreScreen: 'results' } })}
  />;
};

export default ViewResults;
export { DynamicSummary };